import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadPipeline() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [stages, setStages] = useState([]);
  const [showOppModal, setShowOppModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [oppForm, setOppForm] = useState({ lead_id: '', name: '', module: '', service: '', priority: 'medium', expected_value: '', expected_closing_date: '', pipeline_stage_id: '' });

  const fetchPipeline = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/leads/opportunities/pipeline`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setPipeline(Array.isArray(d) ? d : []))
      .catch(() => setPipeline([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads/settings/pipeline-stages`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setStages(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads?per_page=200&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setLeads(Array.isArray(d.data) ? d.data : [])).catch(() => {});
  }, [companyId]);

  const moveStage = (oppId, newStageId) => {
    fetch(`${API}/companies/${companyId}/leads/opportunities/${oppId}/change-stage`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pipeline_stage_id: newStageId }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) fetchPipeline(); });
  };

  const createOpp = () => {
    fetch(`${API}/companies/${companyId}/leads/opportunities`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(oppForm),
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d) { setShowOppModal(false); setOppForm({ lead_id: '', name: '', module: '', service: '', priority: 'medium', expected_value: '', expected_closing_date: '', pipeline_stage_id: '' }); fetchPipeline(); }
    });
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Pipeline View</h2>
        <button onClick={() => setShowOppModal(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>+ New Opportunity</button>
      </div>

      {initialLoading ? <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading pipeline...</div> : (
        <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 16 }}>
          {pipeline.map(stage => (
            <div key={stage.id} style={{ minWidth: 280, maxWidth: 320, flexShrink: 0, background: '#f9fafb', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: stage.color || '#6366f1', borderRadius: 8, color: '#fff' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{stage.name}</span>
                <span style={{ fontSize: 12, opacity: 0.9 }}>{stage.count} • ${Number(stage.total_value || 0).toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(stage.opportunities || []).map(opp => (
                  <div key={opp.id} style={{ background: '#fff', borderRadius: 8, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,.06)', cursor: 'pointer', borderLeft: `3px solid ${priorityColors[opp.priority] || '#6b7280'}` }}
                    onClick={() => navigate(`/company/leads/${opp.lead_id}`)}>
                    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>{opp.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{opp.lead?.lead_name || ''}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: '#10b981', fontSize: 14 }}>{opp.expected_value ? '$' + Number(opp.expected_value).toLocaleString() : '—'}</span>
                      {opp.expected_closing_date && <span style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(opp.expected_closing_date).toLocaleDateString()}</span>}
                    </div>
                    {/* Stage move buttons */}
                    <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                      {stages.filter(s => s.id !== stage.id).slice(0, 3).map(s => (
                        <button key={s.id} onClick={(e) => { e.stopPropagation(); moveStage(opp.id, s.id); }}
                          style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#6b7280' }}
                          title={'Move to ' + s.name}>
                          → {s.name.length > 10 ? s.name.substring(0, 10) + '…' : s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {(!stage.opportunities || stage.opportunities.length === 0) && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#d1d5db', fontSize: 13 }}>No opportunities</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary row */}
      {!loading && (
        <div style={{ marginTop: 20, display: 'flex', gap: 20, fontSize: 14, color: '#6b7280' }}>
          <span>Total Opportunities: <strong>{pipeline.reduce((s, p) => s + (p.count || 0), 0)}</strong></span>
          <span>Total Pipeline Value: <strong style={{ color: '#10b981' }}>${pipeline.reduce((s, p) => s + Number(p.total_value || 0), 0).toLocaleString()}</strong></span>
          <span>Weighted Value: <strong style={{ color: '#6366f1' }}>${pipeline.reduce((s, p) => s + Number(p.weighted_value || 0), 0).toLocaleString()}</strong></span>
        </div>
      )}

      {/* New Opportunity Modal */}
      {showOppModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 520, maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>New Opportunity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Lead *</label>
                <select style={inputStyle} value={oppForm.lead_id} onChange={e => setOppForm({ ...oppForm, lead_id: e.target.value })}>
                  <option value="">Select lead</option>{leads.map(l => <option key={l.id} value={l.id}>{l.lead_name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Opportunity Name *</label><input style={inputStyle} value={oppForm.name} onChange={e => setOppForm({ ...oppForm, name: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Module</label><input style={inputStyle} value={oppForm.module} onChange={e => setOppForm({ ...oppForm, module: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Service</label><input style={inputStyle} value={oppForm.service} onChange={e => setOppForm({ ...oppForm, service: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Expected Value</label><input style={inputStyle} type="number" value={oppForm.expected_value} onChange={e => setOppForm({ ...oppForm, expected_value: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Expected Closing</label><input style={inputStyle} type="date" value={oppForm.expected_closing_date} onChange={e => setOppForm({ ...oppForm, expected_closing_date: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Pipeline Stage</label>
                <select style={inputStyle} value={oppForm.pipeline_stage_id} onChange={e => setOppForm({ ...oppForm, pipeline_stage_id: e.target.value })}>
                  <option value="">First stage</option>{stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                <select style={inputStyle} value={oppForm.priority} onChange={e => setOppForm({ ...oppForm, priority: e.target.value })}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowOppModal(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createOpp} disabled={!oppForm.lead_id || !oppForm.name} style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: oppForm.lead_id && oppForm.name ? 1 : 0.5 }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
